import React from "react";
import classNames from "classnames";
import { SelectList } from "@common/selectList";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { useRetrieveResourcesByUuids } from "@planzone/view-models";
import { Identifiable, Resource, ResourceTypes } from "@planzone/dto";
import { ActivityWatcher } from "@common/activityWatcher";
import { ResourceAvatar } from "@common/resourceAvatar";
import { AddAllocationIcon } from "@common/assets/addAllocationIcon";
import { Props } from "./types";
import styles from "./styles.scss";

const SEPARATOR = ":::";

function ResourceForm({ value, readOnly, disabled, submit }: Props) {
  const values = value.split(SEPARATOR).filter((val) => val.length > 0);
  // Only the picked resources are asked, by their uuid: a deleted one shows its uuid.
  const { resources, pendingUuids, missingUuids, retrieveResourcesByUuids } =
    useRetrieveResourcesByUuids();
  const resolve = (uuid: string) =>
    resources[uuid]?.name || (missingUuids.includes(uuid) ? uuid : "");
  const selectedResources = values.reduce<Identifiable<Resource>[]>(
    (nextRes, uuid) =>
      resources[uuid] ? [...nextRes, resources[uuid]] : nextRes,
    [],
  );

  React.useEffect(() => {
    const unknownUuids = values.filter(
      (uuid) =>
        !resources[uuid] &&
        !pendingUuids.includes(uuid) &&
        !missingUuids.includes(uuid),
    );

    unknownUuids.length > 0 &&
      retrieveResourcesByUuids({ uuids: unknownUuids });
  }, [value]);

  return readOnly ? (
    <div className={classNames(styles.listForm, styles.readOnly)}>
      <p className={styles.input}>
        {!!value ? values.map(resolve).filter(Boolean).join(", ") : ""}
      </p>
    </div>
  ) : (
    <SelectList
      rootClassName={classNames(styles.listForm, {
        [styles.disabled]: disabled,
      })}
      listClassName={styles.listRoot}
      isDisabled={disabled}
      multiple
    >
      {({ isOpen, select: s, show, hide }) => ({
        input: (
          <ul
            className={classNames(styles.input, { [styles.open]: isOpen })}
            onClick={(e) => {
              if (!readOnly && !disabled) {
                e.stopPropagation();
                (isOpen ? hide : show)();
              }
            }}
          >
            {[...selectedResources]
              .sort((a, b) => a.name.localeCompare(b.name))
              .sort((a, b) => {
                const priority = {
                  [ResourceTypes.HUMAN]: 1,
                  [ResourceTypes.VIRTUAL]: 2,
                  [ResourceTypes.MATERIAL]: 3,
                };

                return priority[a.type] - priority[b.type];
              })
              .slice(0, 2)
              .map((resource) => (
                <li key={resource.uuid} className={styles.allocation}>
                  <ResourceAvatar resource={resource} />
                  <div className={styles.infoBubble}>
                    <p>{resource.name}</p>
                  </div>
                </li>
              ))}
            {selectedResources.slice(2).length > 0 && (
              <li className={classNames(styles.allocation, styles.summary)}>
                <p>+{selectedResources.slice(2).length}</p>
                <div className={styles.infoBubble}>
                  {selectedResources.slice(2).map((resource, key) => (
                    <p key={key}>{resource.name}</p>
                  ))}
                </div>
              </li>
            )}
            {!readOnly && !disabled && selectedResources.length === 0 && (
              <li className={styles.allocation}>
                <div className={styles.menu}>
                  <div>
                    <AddAllocationIcon className={styles.addAllocationIcon} />
                  </div>
                </div>
              </li>
            )}
          </ul>
        ),
        list: (
          <div onClick={(e) => e.stopPropagation()}>
            <ResourceAllocationMenu
              allocatedResources={selectedResources}
              openedViews={["resources"]}
              select={(res) =>
                s(
                  submit(
                    [...new Set([...values, ...res.map((r) => r.uuid)])].join(
                      SEPARATOR,
                    ),
                  ),
                )
              }
              unSelect={(res) =>
                s(
                  submit(
                    values
                      .filter((v) => !res.find((r) => r.uuid === v))
                      .join(SEPARATOR),
                  ),
                )
              }
            />
          </div>
        ),
      })}
    </SelectList>
  );
}

export { ResourceForm };
